/**
 * <TrustStrip> — small credibility badges row, placed near conversion CTAs.
 *
 *   SOC 2 Certified    End-to-end encrypted    GDPR Compliant
 *
 * Each badge uses a small lucide icon to telegraph "verified/secure".
 */
import React from 'react';
import styles from './TrustStrip.module.css';

export type BadgeKind = 'check' | 'lock' | 'shield';

export interface TrustBadge {
  kind: BadgeKind;
  label: string;
}

interface TrustStripProps {
  badges?: TrustBadge[];
  className?: string;
}

const DEFAULT_BADGES: TrustBadge[] = [
  { kind: 'check',  label: 'SOC 2 Certified' },
  { kind: 'lock',   label: 'End-to-end encrypted' },
  { kind: 'shield', label: 'GDPR Compliant' },
];

export const TrustStrip: React.FC<TrustStripProps> = ({
  badges = DEFAULT_BADGES,
  className = '',
}) => {
  return (
    <div className={`${styles.strip} ${className}`} role="list" aria-label="Security and compliance">
      {badges.map((b, i) => (
        <span key={i} className={styles.badge} role="listitem">
          <BadgeIcon kind={b.kind} />
          {b.label}
        </span>
      ))}
    </div>
  );
};

const BadgeIcon: React.FC<{ kind: BadgeKind }> = ({ kind }) => {
  switch (kind) {
    case 'check':
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"
             stroke="currentColor" strokeWidth="2"
             strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="9" />
        </svg>
      );
    case 'lock':
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"
             stroke="currentColor" strokeWidth="2"
             strokeLinecap="round" strokeLinejoin="round">
          <rect x="4" y="11" width="16" height="9" rx="2" />
          <path d="M8 11V8a4 4 0 018 0v3" />
        </svg>
      );
    case 'shield':
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"
             stroke="currentColor" strokeWidth="2"
             strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 3l8 3v5c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-3z" />
        </svg>
      );
  }
};
